import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import './ViewFoundation.css'; // Reuse foundation styles
import BackToDashboard from '../components/BackToDashboard';

function ViewBrandIdentity() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [brandData, setBrandData] = useState(null);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch('/api/brand/get-complete-brand-identity', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setBrandData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load brand identity data');
      }
    } catch (err) {
      setError('Error loading brand identity: ' + err.message);
    }

    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your brand identity...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/dashboard/branding')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <>
      <div className="no-print">
        <Container className="py-4">
          <BackToDashboard module="branding" />
          <Row>
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Your Complete Brand Identity</h1>
                <div>
                  <Button variant="primary" onClick={handlePrint} className="me-2">
                    Print Brand Guide
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/dashboard/branding')}>
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="foundation-content py-4">

        {/* Brand Archetypes */}
        {brandData?.archetypes?.primary && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Brand Archetype</h2>

              <div className="mb-3">
                <strong>Primary Archetype:</strong>
                <div className="mt-2 p-3 bg-light rounded">
                  <h4 className="mb-1">{brandData.archetypes.primary.name}</h4>
                  <p className="mb-0 text-muted">Score: {brandData.archetypes.primary.score.toFixed(2)}</p>
                </div>
              </div>

              {brandData.archetypes.secondary && (
                <div className="mb-3">
                  <strong>Secondary Archetype:</strong>
                  <div className="mt-2 p-3 bg-light rounded">
                    <h5 className="mb-1">{brandData.archetypes.secondary.name}</h5>
                    <p className="mb-0 text-muted">Score: {brandData.archetypes.secondary.score.toFixed(2)}</p>
                  </div>
                </div>
              )}

              {brandData.archetypes.tertiary && (
                <div className="mb-0">
                  <strong>Tertiary Archetype:</strong>
                  <div className="mt-2 p-3 bg-light rounded">
                    <h6 className="mb-1">{brandData.archetypes.tertiary.name}</h6>
                    <p className="mb-0 text-muted">Score: {brandData.archetypes.tertiary.score.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Color Palette */}
        {brandData?.color_palette && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Color Palette</h2>

              <Row className="g-3">
                {brandData.color_palette.primary && (
                  <Col xs={6} md={3}>
                    <div className="text-center">
                      <div
                        style={{
                          backgroundColor: brandData.color_palette.primary,
                          height: '100px',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          marginBottom: '8px'
                        }}
                      ></div>
                      <strong>Primary</strong>
                      <div className="text-muted small">{brandData.color_palette.primary}</div>
                    </div>
                  </Col>
                )}

                {brandData.color_palette.secondary && (
                  <Col xs={6} md={3}>
                    <div className="text-center">
                      <div
                        style={{
                          backgroundColor: brandData.color_palette.secondary,
                          height: '100px',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          marginBottom: '8px'
                        }}
                      ></div>
                      <strong>Secondary</strong>
                      <div className="text-muted small">{brandData.color_palette.secondary}</div>
                    </div>
                  </Col>
                )}

                {brandData.color_palette.accent1 && (
                  <Col xs={6} md={3}>
                    <div className="text-center">
                      <div
                        style={{
                          backgroundColor: brandData.color_palette.accent1,
                          height: '100px',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          marginBottom: '8px'
                        }}
                      ></div>
                      <strong>Accent 1</strong>
                      <div className="text-muted small">{brandData.color_palette.accent1}</div>
                    </div>
                  </Col>
                )}

                {brandData.color_palette.accent2 && (
                  <Col xs={6} md={3}>
                    <div className="text-center">
                      <div
                        style={{
                          backgroundColor: brandData.color_palette.accent2,
                          height: '100px',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          marginBottom: '8px'
                        }}
                      ></div>
                      <strong>Accent 2</strong>
                      <div className="text-muted small">{brandData.color_palette.accent2}</div>
                    </div>
                  </Col>
                )}

                {brandData.color_palette.neutral && (
                  <Col xs={6} md={3}>
                    <div className="text-center">
                      <div
                        style={{
                          backgroundColor: brandData.color_palette.neutral,
                          height: '100px',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          marginBottom: '8px'
                        }}
                      ></div>
                      <strong>Neutral</strong>
                      <div className="text-muted small">{brandData.color_palette.neutral}</div>
                    </div>
                  </Col>
                )}

                {brandData.color_palette.background && (
                  <Col xs={6} md={3}>
                    <div className="text-center">
                      <div
                        style={{
                          backgroundColor: brandData.color_palette.background,
                          height: '100px',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          marginBottom: '8px'
                        }}
                      ></div>
                      <strong>Background</strong>
                      <div className="text-muted small">{brandData.color_palette.background}</div>
                    </div>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Typography */}
        {brandData?.typography && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Typography</h2>

              {brandData.typography.heading_font && (
                <div className="mb-3">
                  <strong>Heading Font:</strong>
                  <div className="mt-2 p-3 bg-light rounded">
                    <h3 style={{ fontFamily: brandData.typography.heading_font, marginBottom: '8px' }}>
                      {brandData.typography.heading_font}
                    </h3>
                    <p className="mb-0 text-muted">
                      {brandData.typography.heading_style || 'Used for headlines and titles'}
                    </p>
                  </div>
                </div>
              )}

              {brandData.typography.body_font && (
                <div className="mb-0">
                  <strong>Body Font:</strong>
                  <div className="mt-2 p-3 bg-light rounded">
                    <p style={{ fontFamily: brandData.typography.body_font, marginBottom: '8px', fontSize: '16px' }}>
                      {brandData.typography.body_font}
                    </p>
                    <p className="mb-0 text-muted small">
                      {brandData.typography.body_style || 'Used for body text and paragraphs'}
                    </p>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Logo Direction */}
        {brandData?.logo_direction && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Logo Direction</h2>

              {brandData.logo_direction.primary_direction && (
                <div className="mb-3">
                  <strong>Primary Style:</strong>
                  <p className="mb-0 mt-2">{brandData.logo_direction.primary_direction}</p>
                </div>
              )}

              {brandData.logo_direction.symbol_approach && (
                <div className="mb-3">
                  <strong>Symbol Approach:</strong>
                  <p className="mb-0 mt-2">{brandData.logo_direction.symbol_approach}</p>
                </div>
              )}

              {brandData.logo_direction.wordmark_style && (
                <div className="mb-3">
                  <strong>Wordmark Style:</strong>
                  <p className="mb-0 mt-2">{brandData.logo_direction.wordmark_style}</p>
                </div>
              )}

              {brandData.logo_direction.implementation_notes && (
                <div className="mb-0">
                  <strong>Implementation Notes:</strong>
                  <p className="mb-0 mt-2 preserve-whitespace">{brandData.logo_direction.implementation_notes}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Brand Marker */}
        {brandData?.iconography && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Brand Marker</h2>

              {/* New brand_marker format — check via the raw iconography data from API */}
              {brandData.iconography.primary_image ? (
                <div className="d-flex align-items-center gap-3">
                  <div style={{ backgroundColor: '#e8e8e8', borderRadius: '8px', padding: '10px' }}>
                    <img src={brandData.iconography.primary_image} alt="Brand marker" style={{ width: 80, height: 80, objectFit: 'contain' }} />
                  </div>
                  <div>
                    <p className="mb-1"><strong>Position:</strong> {(brandData.iconography.position || 'bottom-right').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</p>
                    <p className="mb-1"><strong>Size:</strong> {(brandData.iconography.size || 'medium').charAt(0).toUpperCase() + (brandData.iconography.size || 'medium').slice(1)}</p>
                    <p className="mb-0"><strong>Opacity:</strong> {brandData.iconography.opacity || 85}%</p>
                  </div>
                </div>
              ) : brandData.iconography.visual_style ? (
                /* Legacy iconography format */
                <div>
                  <p className="mb-1"><strong>Visual Style:</strong> {brandData.iconography.visual_style.direction || ''}</p>
                  {brandData.icon_images && brandData.icon_images.length > 0 && (
                    <div className="d-flex gap-2 flex-wrap mt-2">
                      {brandData.icon_images.map((image, index) => (
                        <img
                          key={index}
                          src={image.url ? image.url : image}
                          alt={`Icon ${index + 1}`}
                          style={{ width: 48, height: 48, borderRadius: '4px', border: '1px solid #dee2e6' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </Card.Body>
          </Card>
        )}

        {/* Brand Style & Texture */}
        {(brandData?.brand_style?.length > 0 || brandData?.brand_texture?.length > 0) && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Brand Style & Texture</h2>

              {brandData.brand_style?.length > 0 && (
                <div className="mb-3">
                  <strong>Style Words:</strong>
                  <div className="mt-2">
                    {brandData.brand_style.map((word, index) => (
                      <Badge key={index} bg="primary" className="me-2 mb-2" style={{ fontSize: '1rem', padding: '8px 16px' }}>
                        {word}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {brandData.brand_texture?.length > 0 && (
                <div className="mb-0">
                  <strong>Texture Words:</strong>
                  <div className="mt-2">
                    {brandData.brand_texture.map((word, index) => (
                      <Badge key={index} bg="secondary" className="me-2 mb-2" style={{ fontSize: '1rem', padding: '8px 16px' }}>
                        {word}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Brand Voice */}
        {brandData?.brand_voice && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Brand Voice</h2>
              <div className="preserve-whitespace" style={{ whiteSpace: 'pre-wrap' }}>
                {brandData.brand_voice}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Visual Mood Board */}
        {brandData?.mood_board?.images?.length > 0 && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Visual Mood Board</h2>
              <p className="text-muted mb-3">
                These images capture the visual aesthetic and emotional feel of your brand.
              </p>

              <Row className="g-3">
                {brandData.mood_board.images.map((image, index) => (
                  <Col key={index} xs={12} sm={6} md={4}>
                    <div
                      style={{
                        width: '100%',
                        paddingBottom: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                      }}
                    >
                      <img
                        src={image.url ? `http://localhost:8000${image.url}` : image}
                        alt={`Mood board ${index + 1}`}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          console.error('Failed to load mood board image:', image);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Summary Footer */}
        <Card className="mb-4 print-section border-primary">
          <Card.Body className="bg-light">
            <h3 className="mb-3">✨ Your Brand Identity Complete</h3>
            <p className="mb-2">
              You've successfully defined your complete brand identity including archetype, colors, typography,
              logo direction, brand marker, style, texture, voice, and visual mood board. This comprehensive
              brand guide will ensure consistency across all your marketing materials and communications.
            </p>
            <p className="mb-0 text-muted">
              <small>Generated by AI Profit Engineer • Module 3: Brand Identity</small>
            </p>
          </Card.Body>
        </Card>

      </Container>
    </>
  );
}

export default ViewBrandIdentity;
